import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMyApiRequestsQuery, useRetriggerMyApiRequestWebhookMutation } from "@/generated/graphql";
import { useWindowScroll } from "@uidotdev/usehooks";
import { formatDateTime } from "@/lib/format";
import { ApiRequestCard } from "@/components/ApiRequestCard";
import { Card } from "@/components/ui/card";
import { Clock, Check, X, AlertTriangle, TimerOff, Activity, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

const ApiRequests = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const PAGE_SIZE = 24;
  const { data, loading, error, fetchMore } = useMyApiRequestsQuery({
    variables: { limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });
  const [retriggerWebhook] = useRetriggerMyApiRequestWebhookMutation();
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const [retriggeringId, setRetriggeringId] = useState<string | null>(null);
  const [scroll] = useWindowScroll();
  const scrollY = scroll?.y ?? 0;

  const apiRequests = data?.myAPIRequests ?? [];

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'processing':
        return t('apiRequests.status.processing');
      case 'completed':
        return t('apiRequests.status.completed');
      case 'failed':
        return t('apiRequests.status.failed');
      case 'pending':
        return t('apiRequests.status.pending');
      case 'expired':
        return t('apiRequests.status.expired');
      default:
        return status;
    }
  };

  const formattedRequests = useMemo(() => (
    apiRequests.map(request => ({
      id: request.id,
      name: request.name || t('apiRequests.noName'),
      dateLabel: formatDateTime(request.createdAt),
      processStatus: request.process_status,
      baseVideoUrl: request.base_video_url,
      webhookUrl: request.webhook_url,
      apiKeyName: request.api_key_name,
      thumbnail: request.thumbnail_url || undefined,
      clipsCount: request.clips?.length || 0,
      statusLabel: getStatusLabel(request.process_status),
      statusVariant: getStatusVariant(request.process_status),
      statusIcon: getStatusIcon(request.process_status),
    }))
  ), [apiRequests, t]);

  const stats = useMemo(() => {
    const processing = formattedRequests.filter(r => r.processStatus === 'processing' || r.processStatus === 'pending').length;
    const completed = formattedRequests.filter(r => r.processStatus === 'completed').length;
    const failed = formattedRequests.filter(r => r.processStatus === 'failed').length;
    return { total: formattedRequests.length, processing, completed, failed };
  }, [formattedRequests]);

  const handleRetriggerWebhook = async (id: string) => {
    setRetriggeringId(id);
    try {
      const { data } = await retriggerWebhook({ variables: { id } });
      if (data?.retriggerMyAPIRequestWebhook?.success) {
        toast({ title: t('apiRequests.webhookRetriggered') });
      } else {
        toast({ title: t('apiRequests.webhookRetriggerFailed'), variant: "destructive" });
      }
    } catch {
      toast({ title: t('apiRequests.webhookRetriggerFailed'), variant: "destructive" });
    } finally {
      setRetriggeringId(null);
    }
  };

  const handleLoadMore = async () => {
    if (isFetchingMoreRef.current || loading || error || !hasMore) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      const prevCount = apiRequests.length;
      const res = await fetchMore({
        variables: {
          limit: PAGE_SIZE,
          offset: prevCount,
        },
      });
      const fetched = res.data?.myAPIRequests?.length ?? 0;
      if (fetched < PAGE_SIZE) setHasMore(false);
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    if (loading || !hasMore) return;
    const currentY = scrollY ?? 0;
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const docH = document.documentElement.scrollHeight || document.body.scrollHeight;
    const distanceFromBottom = docH - (currentY + viewportH);
    if (distanceFromBottom < 600) {
      handleLoadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY, hasMore, loading]);

  useEffect(() => {
    if (!loading && apiRequests.length > 0 && apiRequests.length < PAGE_SIZE) {
      setHasMore(false);
    }
     
  }, [loading, apiRequests.length]);

  return (
    <Layout>
      <div className="flex-1 p-4 lg:p-6 space-y-6">
        {/* Header with stats */}
        <div className="space-y-4">
          <h2 className="text-xl lg:text-2xl font-bold">{t('apiRequests.title')}</h2>

          {!loading && !error && stats.total > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Activity className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-lg font-semibold leading-none">{stats.total}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{t('apiRequests.stats.total')}</p>
                </div>
              </Card>
              <Card className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-lg font-semibold leading-none">{stats.processing}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{t('apiRequests.status.processing')}</p>
                </div>
              </Card>
              <Card className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-lg font-semibold leading-none">{stats.completed}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{t('apiRequests.status.completed')}</p>
                </div>
              </Card>
              <Card className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-4 h-4 text-red-400" />
                </div>
                <div>
                  <p className="text-lg font-semibold leading-none">{stats.failed}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{t('apiRequests.status.failed')}</p>
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* Loading skeletons */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, idx) => (
              <Card key={`skeleton-${idx}`} className="overflow-hidden animate-pulse">
                <div className="aspect-video bg-secondary" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-secondary rounded w-3/4" />
                  <div className="h-3 bg-secondary rounded w-1/2" />
                  <div className="h-px bg-border/50 mt-2" />
                  <div className="h-3 bg-secondary rounded w-2/3" />
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <Card className="p-8 text-center">
            <XCircle className="w-8 h-8 text-destructive mx-auto mb-3" />
            <p className="text-sm text-destructive">{t('apiRequests.failedToLoad')}</p>
          </Card>
        )}

        {/* Request cards grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {formattedRequests.map(request => (
              <ApiRequestCard
                key={request.id}
                id={request.id}
                name={request.name}
                dateLabel={request.dateLabel}
                processStatus={request.processStatus}
                statusLabel={request.statusLabel}
                statusVariant={request.statusVariant}
                statusIcon={request.statusIcon}
                thumbnail={request.thumbnail}
                baseVideoUrl={request.baseVideoUrl}
                webhookUrl={request.webhookUrl}
                apiKeyName={request.apiKeyName}
                clipsCount={request.clipsCount}
                onRetriggerWebhook={handleRetriggerWebhook}
                isRetriggering={retriggeringId === request.id}
              />
            ))}

            {formattedRequests.length === 0 && (
              <Card className="col-span-full p-12 text-center">
                <Activity className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{t('apiRequests.noRequests')}</p>
              </Card>
            )}

            {isFetchingMore && (
              <div className="col-span-full flex items-center justify-center gap-2 py-6">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{t('apiRequests.loadingMore')}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

// Helper functions
function getStatusVariant(status: string): string {
  switch (status) {
    case 'processing':
    case 'pending':
      return 'secondary';
    case 'completed':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'expired':
      return 'outline';
    default:
      return 'outline';
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'processing':
    case 'pending':
      return Clock;
    case 'completed':
      return Check;
    case 'failed':
      return X;
    case 'expired':
      return TimerOff;
    default:
      return AlertTriangle;
  }
}

export default ApiRequests;
